import { useCallback, useState } from 'react'
import { authApi } from '../lib/api'
import type { AuthUser, LoginCredentials, Tenant } from '../types'

interface StoredAuth {
  user: AuthUser
  tenants: Tenant[]
}

function getInitialAuth(): StoredAuth | null {
  const token = localStorage.getItem('token')
  const stored = localStorage.getItem('auth')
  if (token && stored) {
    try {
      return JSON.parse(stored) as StoredAuth
    } catch {
      localStorage.removeItem('token')
      localStorage.removeItem('auth')
      localStorage.removeItem('selectedTenantId')
    }
  }
  return null
}

export function useAuth() {
  const [auth, setAuth] = useState<StoredAuth | null>(getInitialAuth)
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const user = auth?.user ?? null
  const tenants = auth?.tenants ?? []

  const login = useCallback(async (credentials: LoginCredentials) => {
    setIsLoading(true)
    setError(null)
    try {
      const response = await authApi.login(credentials)
      localStorage.setItem('token', response.token)
      localStorage.setItem('auth', JSON.stringify({ user: response.user, tenants: response.tenants }))

      if (response.tenants.length === 1) {
        localStorage.setItem('selectedTenantId', response.tenants[0].id)
      }

      setAuth({ user: response.user, tenants: response.tenants })
      return true
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Login failed'
      setError(message)
      return false
    } finally {
      setIsLoading(false)
    }
  }, [])

  const selectTenant = useCallback((tenantId: string) => {
    localStorage.setItem('selectedTenantId', tenantId)
  }, [])

  const logout = useCallback(() => {
    localStorage.removeItem('token')
    localStorage.removeItem('auth')
    localStorage.removeItem('selectedTenantId')
    setAuth(null)
    setError(null)
  }, [])

  return {
    user,
    tenants,
    isAuthenticated: !!user,
    isLoading,
    error,
    login,
    logout,
    selectTenant,
  }
}
